import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiMagnifyingGlass, HiChevronDown } from "react-icons/hi2";
import * as userService from "../../utilities/users-service";
import logo from "../../assets/Logo.png";

export default function NavBar({ user, setUser }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the admin menu on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(evt) {
      if (menuRef.current && !menuRef.current.contains(evt.target)) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(evt) {
      if (evt.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  function handleLogOut() {
    setMenuOpen(false);
    userService.logOut();
    setUser(null);
    navigate("/");
  }

  function handleSearch(evt) {
    evt.preventDefault();
    const q = searchTerm.trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : "/");
  }

  const menuItemClass =
    "block px-4 py-2 text-sm text-ink/80 no-underline hover:bg-cream hover:text-seal transition-colors";

  return (
    <header>
      <nav className="pt-7 pb-5 border-b border-ink/10">
        {/* Site width fits 5 mosaic tiles: 5×235 + 4×16 gaps + px-6 = 1287 */}
        <div className="max-w-[1287px] mx-auto px-6 flex flex-row flex-wrap items-center gap-x-6 gap-y-4">
          {/* Wordmark */}
          <Link to="/" className="flex items-center gap-3 no-underline">
            <img src={logo} alt="" className="w-9 h-9 object-contain" />
            <span className="font-display uppercase leading-tight text-ink">
              <span className="block text-[15px] tracking-[0.18em]">
                The Japanese Stamp
              </span>
              <span className="block text-[15px] tracking-[0.18em]">
                Archive
              </span>
            </span>
          </Link>

          {/* Primary links + search; own left-aligned row on mobile, pushed
              right of the wordmark from sm up */}
          <div className="w-full sm:w-auto sm:ml-auto flex flex-row flex-wrap items-center gap-x-7 gap-y-3">
            <Link
              className="no-underline text-ink/80 hover:text-seal transition-colors text-[15px]"
              to="/about"
            >
              About the archive
            </Link>

            {/* Admin dropdown */}
            {user?.isAdmin && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className="flex items-center gap-1 text-ink/80 hover:text-seal transition-colors text-[15px] cursor-pointer"
                >
                  Admin
                  <HiChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${menuOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-3 min-w-[11rem] bg-paper border border-ink/10 rounded-md shadow-lg py-1.5 z-40 animate-fadeIn"
                  >
                    <Link
                      role="menuitem"
                      className={menuItemClass}
                      to="/items/new"
                      onClick={() => setMenuOpen(false)}
                    >
                      Add item
                    </Link>
                    <Link
                      role="menuitem"
                      className={menuItemClass}
                      to="/hidden"
                      onClick={() => setMenuOpen(false)}
                    >
                      Hidden items
                    </Link>
                    <Link
                      role="menuitem"
                      className={menuItemClass}
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                    >
                      Tags
                    </Link>
                    <div className="my-1.5 border-t border-ink/10" />
                    <button
                      role="menuitem"
                      onClick={handleLogOut}
                      className={`${menuItemClass} w-full text-left cursor-pointer`}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            )}

            <form
              onSubmit={handleSearch}
              className="flex items-center border-b border-ink/50 focus-within:border-ink transition-colors"
            >
              <input
                type="search"
                value={searchTerm}
                onChange={(evt) => setSearchTerm(evt.target.value)}
                placeholder="Search"
                aria-label="Search the archive"
                className="w-32 sm:w-40 bg-transparent py-1 text-[15px] text-ink outline-none placeholder:text-ink/50 [&::-webkit-search-cancel-button]:hidden"
              />
              <button
                type="submit"
                aria-label="Search"
                className="text-ink/70 hover:text-seal transition-colors pb-0.5"
              >
                <HiMagnifyingGlass className="w-[18px] h-[18px]" />
              </button>
            </form>
          </div>
        </div>
      </nav>
    </header>
  );
}
